// Parse an uploaded CSV into door rows. A bad row is marked malformed
// and kept; the rest of the file still posts.

export interface ParsedIntakeRow {
  name: string;
  domain: string;
  owner: string;
  evidence: string;
  malformed: boolean;
  line_number: number;
}

const DEFAULT_EVIDENCE = 'CSV upload';

export function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  let index = 0;
  while (index < line.length) {
    const character = line[index];
    if (inQuotes) {
      if (character === '"') {
        if (line[index + 1] === '"') {
          current += '"';
          index += 2;
          continue;
        }
        inQuotes = false;
        index += 1;
        continue;
      }
      current += character;
      index += 1;
      continue;
    }
    if (character === '"') {
      inQuotes = true;
      index += 1;
      continue;
    }
    if (character === ',') {
      cells.push(current);
      current = '';
      index += 1;
      continue;
    }
    current += character;
    index += 1;
  }
  cells.push(current);
  return cells;
}

// The first header that matches any of the accepted spellings. A list exported from
// Salesforce or a sheet rarely says exactly "name" and "owner".
function headerIndex(headers: string[], names: string[]): number {
  const normalised = headers.map(header => header.trim().toLowerCase().replace(/[\s_]+/g, ' '));
  for (const name of names) {
    const found = normalised.indexOf(name);
    if (found >= 0) return found;
  }
  return -1;
}

export const NAME_HEADERS = ['name', 'account', 'account name', 'company', 'company name'];
export const DOMAIN_HEADERS = ['domain', 'website', 'company domain'];
export const OWNER_HEADERS = ['owner', 'strategist', 'sdr owner', 'sdr', 'assigned to'];
export const EVIDENCE_HEADERS = ['evidence'];

export function parseIntakeCsv(text: string): ParsedIntakeRow[] {
  const normalised = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalised.split('\n').filter(line => line.trim().length > 0);
  if (lines.length < 2) return [];

  const headerLine = lines[0];
  if (!headerLine) return [];
  const headers = parseCsvLine(headerLine).map(cell => cell.trim());
  const nameIndex = headerIndex(headers, NAME_HEADERS);
  const domainIndex = headerIndex(headers, DOMAIN_HEADERS);
  const ownerIndex = headerIndex(headers, OWNER_HEADERS);
  const evidenceIndex = headerIndex(headers, EVIDENCE_HEADERS);
  if (nameIndex < 0 || domainIndex < 0) return [];

  const rows: ParsedIntakeRow[] = [];
  for (let lineIndex = 1; lineIndex < lines.length; lineIndex += 1) {
    const line = lines[lineIndex];
    if (!line) continue;
    const cells = parseCsvLine(line);
    const name = (cells[nameIndex] || '').trim();
    const domain = (cells[domainIndex] || '').trim();
    const owner = ownerIndex < 0 ? '' : (cells[ownerIndex] || '').trim();
    const evidenceRaw = evidenceIndex < 0 ? '' : (cells[evidenceIndex] || '').trim();
    const evidence = evidenceRaw || DEFAULT_EVIDENCE;
    const line_number = lineIndex + 1;
    const malformed = !name || !domain;
    rows.push({ name, domain, owner, evidence, malformed, line_number });
  }
  return rows;
}
